/* ===== Ascendia: adaptive video (6 Oct 2026) ===============================
 * One engine for every muted "moving photo" on the shop (IN MOTION cards on the
 * homepage, product-page gallery clips, any future section). Markup comes from
 * snippets/asc-adaptive-video.liquid:
 *   <video data-asc-av muted playsinline loop preload="none"
 *          data-s480 data-s720 data-s1080 data-hls data-asc-poster ...>
 *
 * 1. NOTHING is downloaded until the clip is within 300 px of the screen
 *    (then: poster + file attached; preload="metadata" only on a known-fast line),
 *    and, on the homepage, only after the hero image has arrived.
 * 2. The file is picked for THIS phone and THIS line:
 *      iPhone / Safari (no connection info, native HLS): the HLS stream, which
 *        starts light and climbs to 1080p by itself as the line allows;
 *      Android / Chrome: navigator.connection -> 480p (2G/3G, < 1.6 Mbps),
 *        720p (unknown or < 5 Mbps), 1080p (4G >= 5 Mbps = full quality);
 *      never bigger than the box needs (box width x screen density);
 *      Save-Data / 2G / Reduce Motion: no autoplay, poster + play button.
 * 3. It plays when at least half of it is on screen (inside a carousel too:
 *    a card hidden off to the side does not count) and pauses when it leaves.
 * 4. If the phone refuses autoplay (iPhone Low Power Mode) the poster stays with
 *    a play button, and the FIRST touch anywhere starts every blocked clip on show.
 * 5. If an mp4 has not started 6 s after play, or stalls twice, it steps down
 *    one size (720 -> 480) and continues from the same second.
 * State for CSS: data-asc-av-state = idle | loading | playing | paused | blocked
 * on the <video> and on its host (closest [data-asc-av-host], else the parent).
 * Public: window.ascAV.attach(video) / .play(video) / .scan(root)
 * ========================================================================== */
(function () {
  'use strict';
  if (window.ascAV) return;
  var SEL = 'video[data-asc-av]';
  var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection || {};
  var rm = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var probe = document.createElement('video');
  var canHls = !!(probe.canPlayType && probe.canPlayType('application/vnd.apple.mpegurl'));
  var blocked = [];

  function host(v) { return (v.closest && v.closest('[data-asc-av-host]')) || v.parentNode; }
  function state(v, s) {
    v.setAttribute('data-asc-av-state', s);
    var h = host(v); if (h && h.setAttribute) h.setAttribute('data-asc-av-state', s);
  }
  function slowLine() { var et = conn.effectiveType || ''; return !!conn.saveData || et === 'slow-2g' || et === '2g'; }
  function noAuto(v) { return v.getAttribute('data-asc-av-autoplay') === 'false' || slowLine() || !!(rm && rm.matches); }

  function sizes(v) {
    var out = [];
    [480, 720, 1080].forEach(function (h) { var u = v.getAttribute('data-s' + h); if (u) out.push({ h: h, u: u }); });
    return out;
  }
  /* the mp4 height this line + box deserves */
  function wantHeight(v) {
    var et = conn.effectiveType || '', dl = conn.downlink || 0, want;
    if (et === 'slow-2g' || et === '2g' || et === '3g' || (dl && dl < 1.6)) want = 480;
    else if (et === '4g' && dl >= 5) want = 1080;
    else want = 720; /* unknown connection or 1.6-5 Mbps */
    /* never more pixels than the box shows: rendition width = height x (W/H) */
    var W = +v.getAttribute('width') || 9, H = +v.getAttribute('height') || 16;
    var need = (v.clientWidth || 0) * (window.devicePixelRatio || 1);
    if (need > 0) {
      var fit = 1080;
      [480, 720, 1080].some(function (h) { if (h * W / H >= need * 0.9) { fit = h; return true; } return false; });
      if (fit < want) want = fit;
    }
    return want;
  }
  function pick(v, below) {
    var list = sizes(v), hls = v.getAttribute('data-hls');
    if (!below && canHls && hls && v.getAttribute('data-asc-av-hls') !== 'false') return hls;
    if (!list.length) return hls || '';
    var want = below ? below - 1 : wantHeight(v), best = list[0];
    list.forEach(function (s) { if (s.h <= want) best = s; });
    return best.u;
  }
  function heightOf(v, url) { var h = 0; sizes(v).forEach(function (s) { if (s.u === url) h = s.h; }); return h; }

  function setSrc(v, url, keepTime) {
    if (!url || v.getAttribute('data-asc-av-src') === url) return;
    var t = keepTime ? v.currentTime : 0, wasPlaying = !v.paused;
    v.setAttribute('data-asc-av-src', url);
    v.src = url;
    if (t) v.addEventListener('loadedmetadata', function () { try { v.currentTime = t; } catch (e) {} }, { once: true });
    if (wasPlaying) tryPlay(v);
  }
  function attach(v) {
    var p = v.getAttribute('data-asc-poster');
    if (p != null) { if (!v.getAttribute('poster')) v.setAttribute('poster', p); v.removeAttribute('data-asc-poster'); }
    if (!v.getAttribute('data-asc-av-src')) {
      v.muted = true; v.defaultMuted = true; v.playsInline = true;
      /* a few KB of metadata ahead of time only on a known-fast line; otherwise bytes start when it shows */
      var src = pick(v);
      /* HLS players buffer whole segments even for "metadata", so HLS waits for play() */
      v.preload = (src.indexOf('.m3u8') < 0 && conn.effectiveType === '4g' && (conn.downlink || 0) >= 5 && !conn.saveData) ? 'metadata' : 'none';
      setSrc(v, src);
    }
  }
  function stepDown(v) {
    var cur = v.getAttribute('data-asc-av-src') || '', h = heightOf(v, cur);
    if (!h || h <= 480) return false;
    var lower = pick(v, h);
    if (!lower || lower === cur) return false;
    setSrc(v, lower, true);
    return true;
  }

  function tryPlay(v, gesture) {
    attach(v);
    if (v.getAttribute('data-asc-av-user') === 'paused' && !gesture) return;
    if (!v.paused) { state(v, 'playing'); return; }
    state(v, 'loading'); v.__ascT = Date.now();
    var p;
    try { p = v.play(); } catch (e) { p = null; }
    if (p && p.then) {
      p.then(function () { state(v, 'playing'); }, function (err) {
        if (window.ascAVlog) window.ascAVlog('reject ' + (err && err.name));
        /* AbortError = a pause()/new src interrupted this play(); Safari does this when a slide moves or the page scrolls.
           Still on show -> try again shortly (the watch loop below also catches it). */
        if (err && err.name === 'AbortError') { setTimeout(function () { if (v.__ascVis && v.paused && !noAuto(v)) tryPlay(v); }, 350); return; }
        state(v, 'blocked');
        if (blocked.indexOf(v) < 0) blocked.push(v);
        armGesture();
      });
    }
    clearTimeout(v.__ascDog);
    v.__ascDog = setTimeout(function () {
      if (v.paused || v.readyState < 3) { if (stepDown(v)) tryPlay(v); }
    }, 6000);
  }

  var armed = false;
  function armGesture() {
    if (armed) return; armed = true;
    var evs = ['touchend', 'click', 'keydown', 'pointerup'];
    var go = function () {
      evs.forEach(function (t) { window.removeEventListener(t, go, true); });
      armed = false;
      var list = blocked.slice(); blocked.length = 0;
      list.forEach(function (v) { if (v.__ascVis && v.paused) tryPlay(v, true); });
    };
    evs.forEach(function (t) { window.addEventListener(t, go, { capture: true, passive: true }); });
  }

  var nearIO = 'IntersectionObserver' in window ? new IntersectionObserver(function (es) {
    es.forEach(function (e) {
      if (!e.isIntersecting) return;
      var v = e.target; nearIO.unobserve(v);
      /* hero first: wait for the page's hero image (snippets/asc-lazy-loader gate), then poster + file */
      if (window.ascLazyGate) window.ascLazyGate(function () { attach(v); }); else attach(v);
    });
  }, { rootMargin: '300px 0px' }) : null;
  var visIO = 'IntersectionObserver' in window ? new IntersectionObserver(function (es) {
    es.forEach(function (e) {
      var v = e.target;
      v.__ascVis = e.intersectionRatio >= 0.5;
      if (v.__ascVis) { if (!noAuto(v)) tryPlay(v); else { attach(v); if (v.paused) state(v, 'blocked'); } }
      else if (e.intersectionRatio < 0.25 && !v.paused) { v.pause(); }
    });
  }, { threshold: [0, 0.25, 0.5, 0.75] }) : null;

  function init(v) {
    if (v.__ascAV) return; v.__ascAV = 1;
    state(v, 'idle');
    v.addEventListener('playing', function () { state(v, 'playing'); v.__ascStalls = 0; });
    v.addEventListener('pause', function () { if (v.getAttribute('data-asc-av-state') !== 'blocked') state(v, 'paused'); });
    v.addEventListener('waiting', function () {
      v.__ascStalls = (v.__ascStalls || 0) + 1;
      if (v.__ascStalls >= 2 && stepDown(v)) v.__ascStalls = 0;
    });
    v.addEventListener('error', function () { if (!stepDown(v)) state(v, 'blocked'); });
    var btn = host(v) && host(v).querySelector ? host(v).querySelector('[data-asc-av-play]') : null;
    if (btn) btn.addEventListener('click', function (e) {
      e.preventDefault(); e.stopPropagation();
      if (v.paused) { v.removeAttribute('data-asc-av-user'); tryPlay(v, true); }
      else { v.setAttribute('data-asc-av-user', 'paused'); v.pause(); }
    });
    if (nearIO) { nearIO.observe(v); visIO.observe(v); }
    else { attach(v); if (!noAuto(v)) tryPlay(v); }
  }
  function scan(root) {
    root = root || document;
    if (root.nodeType === 1 && root.matches(SEL)) init(root);
    if (root.querySelectorAll) Array.prototype.forEach.call(root.querySelectorAll(SEL), init);
  }

  /* safety net, once a second: a clip that is on show, not paused by the shopper and not refused
     must be playing (covers interrupted play() calls on iPhone Safari), and the state matches reality */
  setInterval(function () {
    if (document.hidden) return;
    Array.prototype.forEach.call(document.querySelectorAll(SEL), function (v) {
      var st = v.getAttribute('data-asc-av-state');
      if (!v.paused && st !== 'playing' && v.readyState >= 3) state(v, 'playing');
      if (v.paused && v.__ascVis && st !== 'blocked' && (st !== 'loading' || Date.now() - (v.__ascT || 0) > 5000) && !noAuto(v) && v.getAttribute('data-asc-av-user') !== 'paused') tryPlay(v);
      else if (v.paused && st === 'playing') state(v, 'paused');
    });
  }, 1000);

  document.addEventListener('visibilitychange', function () {
    Array.prototype.forEach.call(document.querySelectorAll(SEL), function (v) {
      if (document.hidden) { if (!v.paused) { v.pause(); v.__ascHid = 1; } }
      else if (v.__ascHid || (v.__ascVis && v.paused)) { v.__ascHid = 0; if (v.__ascVis && !noAuto(v)) tryPlay(v); }
    });
  });
  new MutationObserver(function (ms) {
    ms.forEach(function (m) { Array.prototype.forEach.call(m.addedNodes, function (n) { if (n.nodeType === 1) scan(n); }); });
  }).observe(document.documentElement, { childList: true, subtree: true });

  window.ascAV = { attach: attach, play: tryPlay, scan: scan, pick: pick };
  /* ?ascdebug=1 : on-screen state of every clip (testing on real phones / the iOS Simulator) */
  if (/ascdebug=1/.test(location.search + document.cookie)) {
    try { document.cookie = 'ascdebug=1;path=/'; } catch (e) {}
    var dbg = document.createElement('pre'); dbg.style.cssText = 'position:fixed;left:0;right:0;top:0;z-index:2147483647;margin:0;background:rgba(0,0,0,.8);color:#0f0;font:10px/1.3 monospace;padding:4px;pointer-events:none;white-space:pre-wrap';
    /* ?ascdebug=1&ascslide=1 : move the IN MOTION slider one card every 8 s (tests slide-change playback on a real iPhone) */
    if (/ascslide=1/.test(location.search)) setInterval(function () { var el = document.querySelector('lumin-shoppable-videos'); if (el && el.slider) el.slider.slideNext(); }, 8000);
    var log = []; window.ascAVlog = function (m) { log.push(m); if (log.length > 6) log.shift(); };
    setInterval(function () {
      if (!dbg.parentNode && document.body) document.body.appendChild(dbg);
      var sl = document.querySelector('lumin-shoppable-videos'); dbg.textContent = 'hls:' + canHls + ' et:' + (conn.effectiveType || '-') + ' swiper:' + (sl && sl.slider ? sl.slider.activeIndex + '/' + sl.slider.slides.length + ' lock:' + sl.slider.allowTouchMove : 'none') + ' Y:' + Math.round(scrollY) + '\n' + Array.prototype.map.call(document.querySelectorAll(SEL), function (v, i) {
        return i + ' ' + (v.getAttribute('data-asc-av-state') || '-') + ' p:' + v.paused + ' t:' + v.currentTime.toFixed(1) + ' rs:' + v.readyState + ' ns:' + v.networkState + ' vis:' + !!v.__ascVis + ' poster:' + !!v.getAttribute('poster') + ' err:' + (v.error ? v.error.code : '') + ' ' + (v.getAttribute('data-asc-av-src') || '').split('/').pop().slice(0, 18);
      }).join('\n') + '\n' + log.join('\n');
    }, 500);
  }
  scan(document);
})();
